import { Component, effect } from '@angular/core';
import { Store } from '@ngrx/store';
import { ProviderSearch } from './models/providerSearch';
import { DisciplineSearch, ProfessionalSearch } from './models';
import { setProvidersSearchOptions } from './state/providers/providers.actions';
import { setProfessionalsSearchOptions } from './state/professionals/professionals.actions';
import { setDisciplinesSearchOptions } from './state/disciplines/disciplines.actions';
import { restoreAuthState } from './state/auth/auth.actions';
import { ProviderService } from './services/provider.service';
import { DisciplineService } from './services/discipline.service';
import { AuthenticationService } from './services/authentication.service';
import { environment } from '../environments/environment';
import { Router } from '@angular/router';
import { cleanSearchParams } from './shared/helper/helpers-functions';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  title = 'PVT';
  providerSearchParamLoaded: boolean = false;
  professionalSearchParamLoaded: boolean = false;
  disciplineSearchParamLoaded: boolean = false;
  constructor(
    private authenticationService: AuthenticationService,
    private providerService: ProviderService,
    private disciplineService: DisciplineService,
    private store: Store,
    public router: Router
  ) {
    effect(
      () => {
        if (this.authenticationService.triggerAuthSignal()) {
          this.triggerAuthLogic();
          this.authenticationService.resetTriggerAuth();
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.triggerAuthLogic();
    this.loadProvidersSearchOptions();
    this.loadProfessionalsSearchOptions();
    this.loadDisciplinesSearchOptions();
  }

  triggerAuthLogic() {
    if (environment.auth.enabled) {
      this.checkAuthentication();
    } else {
      this.store.dispatch(  
        restoreAuthState({ isAuthenticated: true, authToken: 'dummy-token' })
      );
    }
  }

  private loadProvidersSearchOptions() {
    if (!this.providerSearchParamLoaded) {
      this.providerService
        .getProviderSearch()
        .subscribe((providerSearchResponse) => {
          const providerCleanOptions = cleanSearchParams<ProviderSearch>(
            providerSearchResponse
          );
          this.store.dispatch(
            setProvidersSearchOptions({
              providersSearchOptions: providerCleanOptions,
            })
          );
          this.providerSearchParamLoaded = true;
        });
    }
  }

  private loadProfessionalsSearchOptions() {
    if (!this.professionalSearchParamLoaded) {
      this.providerService
        .getProfessionalSearch()
        .subscribe((professionalSearchResponse) => {
          const professionalCleanOptions =
            cleanSearchParams<ProfessionalSearch>(
              professionalSearchResponse
            );
          this.store.dispatch(
            setProfessionalsSearchOptions({
              professionalsSearchOptions: professionalCleanOptions,
            })
          );
          this.professionalSearchParamLoaded = true;
        });
    }
  }

  private loadDisciplinesSearchOptions() {
    if (!this.disciplineSearchParamLoaded) {
      this.disciplineService
        .getDisciplineSearch()
        .subscribe((disciplineSearchResponse) => {
          const cleanOptions = cleanSearchParams<DisciplineSearch>(
            disciplineSearchResponse
          );
          this.store.dispatch(
            setDisciplinesSearchOptions({
              disciplinesSearchOptions: cleanOptions,
            })
          );
          this.disciplineSearchParamLoaded = true;
        });
    }
  }

  private checkAuthentication() {
    if (this.authenticationService.isAuthenticated()) {
      this.authenticationService.restoreAuthState();
    } else {
      let index = window.location.href.indexOf('code');
      if (index == -1) {
        this.authenticationService.authenticate();
      } else {
        let code = window.location.href.substring(index + 5);
        let state = this.extractStateFromUrl();
        
        this.authenticationService.retrieveToken(code, state).subscribe({
          next: (result) => {
            if (result.token && result.token.access_token) {
              this.authenticationService.storeAuthState(result.token);              
              if (result.state && result.state !== '/home') {
                window.location.href = result.state;
              }
            } else {
              this.authenticationService.authenticate();
            }
          },
          error: (error) => {
            if (error.status == 401) {
              this.authenticationService.authenticate();
            } else if (error.status == 0) {}
          },
        });
      }
    }
  }

  private extractStateFromUrl(): string {
    const url = window.location.href;
    const stateIndex = url.indexOf('state=');
    
    if (stateIndex === -1) {
      return '';
    }
    // Calculate position where the 'state' parameter value starts (skip "state=")
    const stateStart = stateIndex + 6;
    const stateEnd = url.indexOf('&', stateStart);
    
    const stateValue = stateEnd !== -1 
      ? url.substring(stateStart, stateEnd)
      : url.substring(stateStart);
      
    return decodeURIComponent(stateValue);
  }

  isAdminRoute(): boolean {
    return this.router.url.startsWith('/admin');
  }
}
